import {
    Alert,
    Card,
    Chip,
    CircularProgress,
    Container,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Typography,
} from '@mui/material';
import {useState} from 'react';

import PageTitle from '../components/PageTitle';
import {GeneralLayout} from '../layouts/GeneralLayout';
import {useGetAssistantHistoryQuery} from '../store/assistant/assistantApi';

const PAGE_SIZE_OPTIONS = [25, 50, 100];

function formatDate(value) {
    if (!value) return '—';

    return new Intl.DateTimeFormat('ru-RU', {
        dateStyle: 'short',
        timeStyle: 'medium',
    }).format(new Date(value));
}

function roleNames(row) {
    const roles = [];
    if (row.isAdmin) roles.push('Администратор');
    if (row.isUmu) roles.push('УМУ');
    if (row.isDispatcher) roles.push('Диспетчер');
    return roles;
}

export default function AssistantHistoryPage() {
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(25);
    const {data, isLoading, isFetching, isError} = useGetAssistantHistoryQuery({
        page: page + 1,
        pageSize,
    });

    const history = data?.data;
    const rows = history?.items ?? [];

    const handlePageSizeChange = (event) => {
        setPageSize(Number(event.target.value));
        setPage(0);
    };

    return (
        <GeneralLayout>
            <PageTitle title="История общения с ИИ-ассистентом" />

            <Container>
                <Stack direction="column"
                       padding="120px 0px 0px 100px"
                       spacing={2}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
                        <Typography variant="h4" gutterBottom>История общения с ИИ-ассистентом</Typography>
                        {isFetching && !isLoading && <CircularProgress size={20} />}
                    </Stack>

                    {isError && (
                        <Alert severity="error">Не удалось загрузить историю обращений к ИИ-ассистенту.</Alert>
                    )}

                    <Card>
                        {isLoading ? (
                            <Stack alignItems="center" py={10}>
                                <CircularProgress />
                            </Stack>
                        ) : (
                            <>
                                <TableContainer sx={{maxHeight: 'calc(100vh - 360px)', minHeight: 280}}>
                                    <Table
                                        stickyHeader
                                        size="small"
                                        aria-label="История общения с ИИ-ассистентом"
                                        sx={{tableLayout: 'fixed'}}
                                    >
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{width: '14%'}}>Дата и время</TableCell>
                                                <TableCell sx={{width: '18%'}}>Пользователь</TableCell>
                                                <TableCell sx={{width: '22%'}}>Запрос</TableCell>
                                                <TableCell sx={{width: '28%'}}>Ответ ассистента</TableCell>
                                                <TableCell sx={{width: '18%'}}>Результат</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {rows.map((row) => (
                                                <TableRow key={row.id} hover sx={{verticalAlign: 'top'}}>
                                                    <TableCell>
                                                        <Typography variant="body2">{formatDate(row.createdAtUtc)}</Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            ID: {row.id}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" fontWeight={600}>
                                                            {row.username || 'Не указан'}
                                                        </Typography>
                                                        {roleNames(row).map((role) => (
                                                            <Chip key={role} label={role} size="small" sx={{mt: 0.5, mr: 0.5}} />
                                                        ))}
                                                        <Typography variant="caption" color="text.secondary" display="block" mt={0.75}>
                                                            Семестр: {row.semesterId || '—'}
                                                            {row.ipAddress ? ` · IP: ${row.ipAddress}` : ''}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                maxHeight: 220,
                                                                overflow: 'auto',
                                                                whiteSpace: 'pre-wrap',
                                                                overflowWrap: 'anywhere',
                                                            }}
                                                        >
                                                            {row.prompt || '—'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                maxHeight: 220,
                                                                overflow: 'auto',
                                                                whiteSpace: 'pre-wrap',
                                                                overflowWrap: 'anywhere',
                                                            }}
                                                        >
                                                            {row.response || '—'}
                                                        </Typography>
                                                        {row.sources?.length > 0 && (
                                                            <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                                                                Источники: {row.sources.join(', ')}
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Stack direction="row" flexWrap="wrap" gap={0.5}>
                                                            <Chip
                                                                size="small"
                                                                color={row.success ? 'primary' : 'error'}
                                                                label={row.success ? 'Успешно' : 'Ошибка'}
                                                            />
                                                            {!row.accessGranted && (
                                                                <Chip size="small" color="warning" label="Доступ запрещён" />
                                                            )}
                                                        </Stack>
                                                        <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                                                            HTTP {row.httpStatusCode} · {row.durationMs} мс
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary" display="block">
                                                            Модель: {row.model || '—'}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary" display="block">
                                                            Страница: {row.pageTitle || row.route || '—'}
                                                        </Typography>
                                                        {row.conversationId && (
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                display="block"
                                                                sx={{overflowWrap: 'anywhere'}}
                                                            >
                                                                Диалог: {row.conversationId}
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                            {rows.length === 0 && !isError && (
                                                <TableRow>
                                                    <TableCell colSpan={5} align="center" sx={{py: 8}}>
                                                        <Typography color="text.secondary">История обращений пока пуста.</Typography>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <TablePagination
                                    component="div"
                                    count={history?.totalCount ?? 0}
                                    page={page}
                                    onPageChange={(_, nextPage) => setPage(nextPage)}
                                    rowsPerPage={pageSize}
                                    onRowsPerPageChange={handlePageSizeChange}
                                    rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                                    labelRowsPerPage="Строк на странице:"
                                    labelDisplayedRows={({from, to, count}) => `${from}–${to} из ${count}`}
                                />
                            </>
                        )}
                    </Card>
                    <br />
                </Stack>
            </Container>
        </GeneralLayout>
    );
}
